// CTV-840: the display ad sizes a station can download from the Materials
// tab. One entry per size; the layout for each lives in DisplayAd.tsx and the
// picker entry in materialsCatalog.ts, both keyed by DisplayAdSize so a size
// can't exist in one place without the other two.

// CTV-856: the 480x96 is the email signature graphic, a sixth entry that
// shares everything (looks, download, the tracked link) but carries the
// rep's own referral code and goes under an email, not on a web page.
export type DisplayAdSize =
  | '320x50'
  | '728x90'
  | '300x250'
  | '300x600'
  | '1080x1080'
  | '480x96';

export interface DisplayAdSpec {
  /** Nominal ad size in CSS pixels. The PNG is exported at a multiple of this. */
  width: number;
  height: number;
  /** The ad-industry name a station's web team will recognise. */
  name: string;
  /**
   * Whether the layout shows the TV. The two strips do not, so a saved
   * TV-screen still changes nothing there and the page does not offer one.
   */
  hasTv: boolean;
  /**
   * CTV-856: the graphic goes into an email signature. It prints the rep's
   * referral code, links with `utm_medium=email`, and the page shows how to
   * install it instead of the web team's click-through note.
   */
  signature?: boolean;
  /**
   * CTV-856: a PNG density for this size other than DISPLAY_AD_EXPORT_SCALE.
   * The signature exports at 2x. Mail clients place a raw pasted picture at
   * its pixel size (a 2x file filled the whole email, 2026-09-09) and a 1x
   * file was blurry on a retina screen, so Copy Signature puts an HTML
   * snippet on the clipboard instead: the 2x picture with width and height
   * set to the nominal size, inside the link. Download PNG stays 2x for the
   * clients that need a file.
   */
  exportScale?: number;
}

export const DISPLAY_AD_SPECS: Record<DisplayAdSize, DisplayAdSpec> = {
  '320x50': { width: 320, height: 50, name: 'Mobile Leaderboard', hasTv: false },
  '728x90': { width: 728, height: 90, name: 'Leaderboard', hasTv: false },
  '300x250': { width: 300, height: 250, name: 'Medium Rectangle', hasTv: true },
  '300x600': { width: 300, height: 600, name: 'Half Page', hasTv: true },
  '1080x1080': { width: 1080, height: 1080, name: 'Social Square', hasTv: true },
  '480x96': { width: 480, height: 96, name: 'Email Signature', hasTv: false, signature: true, exportScale: 2 },
};

export const DISPLAY_AD_SIZES = Object.keys(DISPLAY_AD_SPECS) as DisplayAdSize[];

/**
 * Pixel multiplier for the downloaded PNG. Four matches the reference set
 * (the "_hi" files) and stays crisp when a station opens the file on a 4K
 * screen; 2x looked soft there (feedback 2026-09-03). Ad servers scale it
 * into the nominal slot. The social square lands at 4320x4320, well inside
 * the canvas limit and about the size of the reference file.
 */
export const DISPLAY_AD_EXPORT_SCALE = 4;

/** The density a size's PNG is saved at: its own, or the reference 4x. */
export function displayAdExportScale(size: DisplayAdSize): number {
  return DISPLAY_AD_SPECS[size].exportScale ?? DISPLAY_AD_EXPORT_SCALE;
}

/**
 * CTV-856: the sizes the email signature can be taken at (feedback
 * 2026-09-09: one rep's "right size" is another's "too big"). One layout,
 * drawn at the standard size and scaled for the rest, so the picture is the
 * same in every proportion. Standard is the 480x96 spec itself.
 */
export type SignatureSize = 'compact' | 'standard';

export const SIGNATURE_SIZES: Record<SignatureSize, { width: number; height: number; label: string }> =
  {
    compact: { width: 400, height: 80, label: 'Compact' },
    standard: { width: 480, height: 96, label: 'Standard' },
  };

export const SIGNATURE_SIZE_ORDER: SignatureSize[] = ['compact', 'standard'];

/** The widest an ad may render on the Materials page before it is scaled down. */
const PREVIEW_MAX_WIDTH = 760;

/**
 * How much to scale the on-page preview. Every web size shows at its real
 * pixel size; the social square is scaled to fit beside the picker. The PNG
 * is always exported from the unscaled node.
 */
export function displayAdPreviewScale(size: DisplayAdSize): number {
  const { width } = DISPLAY_AD_SPECS[size];
  return Math.min(1, PREVIEW_MAX_WIDTH / width);
}

/** "300×250 · Medium Rectangle", spelled the same way as the picker card. */
export function displayAdLabel(size: DisplayAdSize): string {
  const { width, height, name } = DISPLAY_AD_SPECS[size];
  return `${width}×${height} · ${name}`;
}
